/**
 * M4S-5777 (X101) — hide answers nobody chose, for the whole crosstab or one question.
 * Spec: docs/pdlc/crosstabs-reading.md §X101.
 *
 * ### The rule, and its one home
 *
 * - **What is hidden.** An answer row (`kind: 'option'`) with no respondent counted in any column.
 *   Total holds everyone the table counts, so "none in any column" is "none in Total"; reading every
 *   cell also covers a table whose Total is hidden or twinned (X90). Nets, statistics, bases and the
 *   box rows are never hidden: a net of empty answers still says 0%, which is a finding.
 * - **Where.** Ordinary questions and a matrix's By Row statement tables. A matrix drawn By Column
 *   (statements as rows), a matrix's summary tables, ranked and allocation tables keep every row:
 *   their rows are not "answers somebody could choose", so an empty one is information. So does a
 *   variable's own table: an empty group says its definition reaches nobody (the rail flags it).
 * - **Which questions.** The question's own choice (`perQuestion[QID]`, true hides, false keeps)
 *   wins over the crosstab-wide switch (`all`). One read, `hidesEmptyAnswers`.
 * - **Filtered, never removed.** The rows are left out of the table the compute returns; no answer
 *   order, rename or edit is touched, so a saved or manual order comes back as it was when the
 *   switch goes off. Live filters recompute the table, so an answer can appear and disappear as a
 *   filter changes: it is hidden exactly while nobody in the filtered table chose it.
 * - **Weighted tables.** The counts read are the table's own; on a weighted table an answer is
 *   empty when its weighted count is 0 in every column, which differs from the unweighted count
 *   only for a respondent carrying weight 0 (weights from a variable with no value), whom the
 *   weighting already flags.
 *
 * ### Record state
 *
 * Content, not a display preference: it changes what the crosstab delivers, so two people must see
 * the same rows and every export must leave out the same ones. Saved as one block,
 * `ai_layout.state.emptyAnswers` (`{ all?: true, perQuestion?: { QID: boolean } }`), parsed here
 * on the write and every read. In settings it is two keys, `hideEmptyAnswers` and
 * `perQuestionHideEmpty` (compute-inputs.ts, record and compute keys, so a change waits for Run).
 *
 * ### Who never applies it
 *
 * The iOOH deck builds from its own compute with fixed options, and Slide Creator's `/computed`
 * passes no such key: both read a scale's answers by position, so a dropped answer would move a
 * figure. They keep every answer.
 *
 * Pure.
 */

import type { Block } from '@/app/(private)/ai/components/Crosstabs/data';

/** The record block. Absent (null) is "nothing hidden". */
export interface EmptyAnswersRecord {
    /** The crosstab-wide switch, stored only when on. */
    all?: true;
    /** Per question, upper-cased qid: true hides its empty answers, false keeps them. */
    perQuestion?: Record<string, boolean>;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/** A per-question map in canonical form: upper-cased qids in sorted order, booleans only. */
export function parsePerQuestionHideEmpty(raw: unknown): Record<string, boolean> {
    if (!isPlainObject(raw)) return {};
    const entries: [string, boolean][] = [];
    for (const [key, v] of Object.entries(raw)) {
        const qid = key.trim().toUpperCase();
        if (!qid || typeof v !== 'boolean') continue;
        entries.push([qid, v]);
    }
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return Object.fromEntries(entries);
}

/**
 * The block an untrusted value holds, in canonical form, or null when it hides nothing (so an
 * empty block and none sign alike and are not stored).
 */
export function parseEmptyAnswers(raw: unknown): EmptyAnswersRecord | null {
    if (!isPlainObject(raw)) return null;
    const perQuestion = parsePerQuestionHideEmpty(raw.perQuestion);
    const all = raw.all === true;
    if (!all && Object.keys(perQuestion).length === 0) return null;
    return {
        ...(all ? { all: true as const } : {}),
        ...(Object.keys(perQuestion).length ? { perQuestion } : {}),
    };
}

/** The block a session's settings hold (what a save sends), or null for nothing hidden. */
export function emptyAnswersOf(settings: {
    hideEmptyAnswers?: unknown;
    perQuestionHideEmpty?: unknown;
}): EmptyAnswersRecord | null {
    return parseEmptyAnswers({
        all: settings.hideEmptyAnswers === true,
        perQuestion: settings.perQuestionHideEmpty,
    });
}

/** The two settings a record's block (or none) means, for the hydrate and a copy. */
export function emptyAnswersFields(raw: unknown): {
    hideEmptyAnswers: boolean;
    perQuestionHideEmpty: Record<string, boolean>;
} {
    const block = parseEmptyAnswers(raw);
    return {
        hideEmptyAnswers: block?.all === true,
        perQuestionHideEmpty: block?.perQuestion ?? {},
    };
}

/** Whether a question's tables hide their empty answers: its own choice, else the switch. */
export function hidesEmptyAnswers(
    qid: string,
    opts: { hideEmptyAnswers?: boolean; perQuestionHideEmpty?: Record<string, boolean> },
): boolean {
    // Read through the parse: a setting can hold any spelling of a qid (compute is handed it as is).
    const own = parsePerQuestionHideEmpty(opts.perQuestionHideEmpty)[qid.toUpperCase()];
    return typeof own === 'boolean' ? own : opts.hideEmptyAnswers === true;
}

/**
 * The per-question map with one question's choice set. A choice equal to the crosstab-wide switch
 * is removed ("follow the switch"), so the map holds only exceptions.
 */
export function withQuestionHideEmpty(
    map: Record<string, boolean> | undefined,
    qid: string,
    hide: boolean,
    all: boolean,
): Record<string, boolean> {
    const next = { ...parsePerQuestionHideEmpty(map) };
    const key = qid.toUpperCase();
    if (hide === all) delete next[key];
    else next[key] = hide;
    return parsePerQuestionHideEmpty(next);
}

/** A variable's own table: its empty groups say its definition reaches nobody, so they stay. */
const VARIABLE_KINDS = new Set(['logic', 'bucketing', 'categorical-bucketing', 'formula']);

/** Whether a table may hide answers at all (see "Where" above). */
export function canHideEmptyIn(
    block: Pick<Block, 'summary' | 'rank' | 'allocation' | 'matrixGroup' | 'kind'>,
    byColumn: boolean,
): boolean {
    if (block.summary || block.rank || block.allocation) return false;
    if (VARIABLE_KINDS.has(block.kind)) return false;
    if (block.matrixGroup && byColumn) return false;
    return true;
}

/** Whether nobody in any column chose this answer. */
export function isEmptyAnswerRow(row: Block['rows'][number]): boolean {
    if (row.kind !== 'option' || row.cells.length === 0) return false;
    return row.cells.every((c) => !c.count);
}

/**
 * The table without its empty answers, `hiddenEmpty` saying how many went. The same block when none
 * is empty, so an unchanged table is the same object.
 */
export function dropEmptyAnswers(block: Block): Block {
    const rows = block.rows.filter((r) => !isEmptyAnswerRow(r));
    const gone = block.rows.length - rows.length;
    if (gone === 0) return block;
    return { ...block, rows, hiddenEmpty: gone };
}

/** The line the table and every file print under a table that hides answers. */
export function hiddenEmptyNote(count: number): string {
    return count === 1
        ? '1 answer nobody chose is hidden.'
        : `${count} answers nobody chose are hidden.`;
}

/**
 * The one line every file's notes print for the tables that hide answers (`tableNoteLines`), naming
 * each table by its text, never its ID (X95), or null when none hides any.
 */
export function hiddenEmptyNoteLine(
    blocks: readonly Pick<Block, 'text' | 'hiddenEmpty'>[],
): string | null {
    const hiding = blocks.filter((b) => (b.hiddenEmpty ?? 0) > 0);
    if (hiding.length === 0) return null;
    const short = (t: string) => (t.length > 60 ? `${t.slice(0, 60).trimEnd()}…` : t);
    return `Answers nobody chose are hidden: ${hiding
        .map((b) => `${short(b.text)} (${b.hiddenEmpty})`)
        .join('; ')}.`;
}
